import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { type OrganizationSummary, updateReportTemplateOrganizationFolder } from "@api/rest/types-and-hooks";
import { InfoTooltip } from "@components/InfoTooltip";
import { InlineEdit } from "@components/InlineEdit/InlineEdit";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

interface OrganizationEhrSyncFoldersProps {
  templateId: number;
  organizations: readonly OrganizationSummary[];
  onManageOrganizations: () => void;
  onSaved: () => void;
}

function OrgFolderRow({
  templateId,
  org,
  onSaved,
}: {
  templateId: number;
  org: OrganizationSummary;
  onSaved: () => void;
}) {
  // InlineEdit exits edit mode (and renders this value) synchronously on
  // blur, before the mutation even resolves. Setting the optimistic value in
  // onMutate — not onSuccess — closes that gap entirely: React batches it
  // with InlineEdit's own setIsEditing(false), so the new value is already
  // showing the instant edit mode exits, with no round-trip-shaped window
  // where the field reads the stale value. That window mattered beyond
  // visuals: reopening the field during it would have reseeded from the
  // stale prop, silently reverting the just-submitted edit if blurred again
  // before the first request resolved. Rolled back on failure (onError, or
  // a 400 surfaced through onSuccess) to the value captured in onMutate;
  // cleared on success once the server-confirmed prop lands, so a background
  // refetch from a sibling row's save doesn't leave this on stale state.
  const [optimisticFolder, setOptimisticFolder] = useState<string | null>(null);
  const displayedFolder = optimisticFolder ?? org.ehr_sync_parent_folder_name ?? "";

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOptimisticFolder(null);
  }, [org.ehr_sync_parent_folder_name]);

  const mutation = useMutation({
    mutationFn: (ehrSyncParentFolderName: string) =>
      updateReportTemplateOrganizationFolder({
        report_template_id: templateId,
        organization_id: org.id,
        ehr_sync_parent_folder_name: ehrSyncParentFolderName,
      }),
    onMutate: (ehrSyncParentFolderName) => {
      const previousFolder = displayedFolder;
      setOptimisticFolder(ehrSyncParentFolderName);
      return { previousFolder };
    },
    onSuccess: (result, _ehrSyncParentFolderName, context) => {
      if (result.status !== 200) {
        setOptimisticFolder(context.previousFolder);
        notifyError(result.data.detail);
        return;
      }
      notifySuccess(`EHR sync folder updated for ${org.name}`);
      onSaved();
    },
    onError: (_error, _ehrSyncParentFolderName, context) => {
      setOptimisticFolder(context?.previousFolder ?? null);
      notifyError("Failed to update EHR sync folder");
    },
  });

  return (
    <div className="flex items-center gap-3 py-2">
      <span className="w-56 shrink-0 truncate text-sm text-text-primary">{org.name}</span>
      {mutation.isPending ? (
        // A second blur while one save is still in flight would start a
        // second mutation whose own onMutate captures the FIRST mutation's
        // (unconfirmed) optimistic text as its "previous" value. If both
        // then fail, whichever rollback resolves last can restore that
        // never-accepted text instead of the true original. Only one save
        // per row at a time avoids this rather than reconciling two
        // independent rollback chains.
        <span className="flex-1 truncate px-3 text-sm italic text-text-tertiary">
          {displayedFolder || "No EHR sync folder set"} (saving…)
        </span>
      ) : (
        <InlineEdit
          value={displayedFolder}
          onChange={(value) => mutation.mutate(value)}
          placeholder="No EHR sync folder set"
          className="flex-1"
        />
      )}
    </div>
  );
}

// INT-79 follow-up: lets Basecamp admins (CS team) set each org's CentralReach
// learning-tree parent folder directly, without going through Django admin.
// Membership (adding/removing an org) stays a separate action via
// onManageOrganizations (AssignOrgsDialog) — this section is purely about the
// per-org folder value for orgs already assigned to the template.
export const OrganizationEhrSyncFolders: React.FC<OrganizationEhrSyncFoldersProps> = ({
  templateId,
  organizations,
  onManageOrganizations,
  onSaved,
}) => {
  return (
    <div className="mt-8 border-t border-border-subtle pt-6">
      <div className="mb-3 flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold text-text-primary">Organizations</h3>
          <InfoTooltip
            label="About EHR sync folder"
            text="Set an EHR sync folder only for CentralReach organizations. It names the learning-tree folder that synced goals are written to and that goals are read from. Leave it blank for other EHRs."
          />
        </div>
        <button type="button" onClick={onManageOrganizations} className="text-xs text-rio-600 hover:underline">
          Manage organizations
        </button>
      </div>
      {organizations.length === 0 ? (
        <p className="text-sm text-text-tertiary">No organizations assigned.</p>
      ) : (
        <div className="divide-y divide-border-subtle">
          {organizations.map((org) => (
            <OrgFolderRow key={org.id} templateId={templateId} org={org} onSaved={onSaved} />
          ))}
        </div>
      )}
    </div>
  );
};
